<script>
/**
 * SelectProducts
 * @version 1.0.0
 * @since 2.3.0
 */
import { mapActions, mapMutations, mapState } from 'vuex';
import { wizardListenerMixin } from '@/mixins/wizard';
import { debounce } from '@/util/utils';
import ProductsSelectList from '@/views/common/products/ProductsSelectList.vue';
import EzBadge from '@/components/ui/Badge/EzBadge';
import EzFilterList from '@/components/ui/FilterList/EzFilterList';
import EzInput from '@/components/ui/Input/EzInput';
import { LOADING_KEY } from '@/util/constants';
import VPrice from '@/components/v3/elements/VPrice';

const FilterArea = {
  List: 'list',
  Dropdown: 'dropdown',
};

export default {
  mixins: [wizardListenerMixin],
  components: {
    EzInput,
    EzFilterList,
    EzBadge,
    ProductsSelectList,
    VPrice,
  },
  data() {
    return {
      meta: {},
      listFilters: {
        term: null,
      },
      LOADING_KEY,
      dropdownFilters: {},
      FilterArea,
    };
  },
  computed: {
    ...mapState('entities/orders', ['newOrder']),
    ...mapState('entities/users', ['contextId']),
    products() {
      return this.newOrder.products;
    },
    selectedProducts() {
      return this.newOrder.selectedProducts;
    },
    selectedProductsMsg() {
      return this.selectedProducts.length ? 'Products selected:' : 'No products selected';
    },
    venueId() {
      return this.contextId;
    },
    totalPrice() {
      return this.selectedProducts.reduce((a, c) => a + c?.quantity * c?.price || 0, 0);
    },
    filters() {
      return { ...this.listFilters, ...this.dropdownFilters };
    },
  },
  methods: {
    ...mapMutations('entities/orders', [
      'SET_DRAFT_PRODUCTS',
      'SET_PRODUCTS',
      'ADD_SELECTED_PRODUCT_FOR_TRANSFER',
      'REMOVE_SELECTED_PRODUCT_FOR_TRANSFER',
      'UPDATE_PRODUCT_FOR_TRANSFER',
    ]),
    ...mapActions('entities/orders', {
      fetchProducts: 'fetchInventoryTransferItems',
    }),
    onPreviousStep() {
      this.$emit('stepBack');
    },
    onNextStep() {
      this.SET_DRAFT_PRODUCTS(this.selectedProducts);
      this.$emit('stepCompleted');
    },
    async onLoadMore(nextId) {
      const {
        data: { data, meta },
      } = await this.fetchProducts({
        venueId: this.venueId,
        nextId,
        nextValue: this.meta.nextValue,
        loadingKey: LOADING_KEY.FETCH_MORE_PRODUCTS_FOR_VENUE,
        filters: this.filters,
      });
      this.meta = meta;

      if (this.selectedProducts.length) {
        const productIds = [...new Set(this.selectedProducts.map(p => p.id))];
        const dataPr = data.filter(p => !productIds.includes(p.id));

        this.SET_PRODUCTS([...this.products, ...dataPr]);
        return;
      }

      this.SET_PRODUCTS([...this.products, ...data]);
    },
    onProductSelect(product) {
      const checked = !product.checked;
      if (checked) {
        this.ADD_SELECTED_PRODUCT_FOR_TRANSFER(product);
        const { orderedCount } = product;
        this.UPDATE_PRODUCT_FOR_TRANSFER({
          ...product,
          quantity: orderedCount < 1 ? orderedCount : 1,
          checked,
          quantityInvalid: false,
        });
      } else {
        this.REMOVE_SELECTED_PRODUCT_FOR_TRANSFER(product);
      }
    },
    onQuantityChange: debounce(function deb(product, quantity) {
      const selectedProd = this.selectedProducts.find(p => p.id === product.id);

      const isChecked = !!quantity;

      if (!selectedProd && isChecked) {
        this.ADD_SELECTED_PRODUCT_FOR_TRANSFER({ ...product, quantity: product.orderedCount });
      }
      if (selectedProd && !isChecked) {
        this.REMOVE_SELECTED_PRODUCT_FOR_TRANSFER({ ...product, quantity });
      }

      this.UPDATE_PRODUCT_FOR_TRANSFER({
        ...product,
        quantity,
        checked: isChecked,
      });
    }, 300),
    searchForProduct: debounce(async function deb(term) {
      if (!term) {
        await this.resetFilters(FilterArea.List);
        return;
      }

      this.listFilters.term = term;

      const {
        data: { data, meta },
      } = await this.fetchProducts({
        venueId: this.venueId,
        filters: this.filters,
      });

      this.meta = meta;
      this.SET_PRODUCTS(data);
    }, 300),
    async resetFilters(area) {
      if (area === FilterArea.List) {
        Object.keys(this.listFilters).forEach(key => {
          this.listFilters[key] = null;
        });
      } else if (area === FilterArea.Dropdown) {
        this.dropdownFilters = {};
      }

      const {
        data: { data, meta },
      } = await this.fetchProducts({
        venueId: this.venueId,
        filters: this.filters,
      });

      this.meta = meta;

      if (this.selectedProducts.length) {
        const selectedProductIds = [...new Set(this.selectedProducts.map(p => p.id))];
        const newProducts = data.filter(p => !selectedProductIds.includes(p.id));
        this.SET_PRODUCTS([...this.selectedProducts, ...newProducts]);
        return;
      }

      this.SET_PRODUCTS(data);
    },
  },
  watch: {
    selectedProducts: {
      deep: true,
      handler(newVal) {
        const invalidProductQuantity = newVal
          .find(prod => prod.quantity > prod.orderedCount && !prod.canTransferWithNoQuantity);
        if (newVal.length && !invalidProductQuantity) {
          this.enableNextStep();
        } else {
          this.disableNextStep();
        }
      },
    },
  },
  async created() {
    this.SET_PRODUCTS([]);
    const {
      data: { data, meta },
    } = await this.fetchProducts({
      venueId: this.venueId,
    });
    let products = data.map(prod => (
      { ...prod,
        quantity: prod.orderedCount,
        checked: false,
        canTransferWithNoQuantity: prod.canTransferWithNoQuantity
      }));

    this.meta = meta;

    if (this.selectedProducts.length) {
      const productIds = [...new Set(this.selectedProducts.map(p => p.id))];
      products = products.filter(p => !productIds.includes(p.id));

      this.SET_PRODUCTS([...this.selectedProducts, ...products]);
      return;
    }

    this.SET_PRODUCTS(products);
  },
};
</script>

<template>
  <div>
    <div class="select-product__actions-container">
      <div class="select-product__actions u-flex-space">
        <span class="wizard-products__table-actions-filters">
          <ez-filter-list :filters="listFilters" @resetFilter="resetFilters(FilterArea.List)">
            <ez-input
              formKey="product-search"
              name="search"
              label="Search"
              placeholder="Search for a Product"
              @onChange="searchForProduct"
            >
              <template #suffix>
                <font-awesome-icon icon="search" />
              </template>
            </ez-input>
          </ez-filter-list>
        </span>
        <span class="select-products__selected-msg">
          <span> {{ selectedProductsMsg }} </span>
          <ez-badge class="ml-4" :count="selectedProducts.length" type="blue" />
        </span>
        <div></div>
      </div>
    </div>

    <products-select-list
      :products="newOrder.products"
      :next-id="meta.nextId"
      :inventory-transfer="true"
      :loading-key="LOADING_KEY.FETCH_PRODUCTS_FOR_VENUE"
      :loading-more-key="LOADING_KEY.FETCH_MORE_PRODUCTS_FOR_VENUE"
      @productSelect="onProductSelect"
      @quantityChange="onQuantityChange"
      @loadMore="onLoadMore"
    />

    <footer class="select-products__footer">
      <div class="total">
        <span class="total__label">Total</span>
        <v-price class="v-market-price-input__mp-input__mp" :price="totalPrice" />
      </div>
    </footer>
  </div>
</template>

<style lang="scss" scoped>
$border-color: #eceef5;

$actions-top: 64px;
$acttions-top-mobile: 110px;
$actions-height: 64px;
$actions-height-mobile: 90px;

$action-min-width: 240px;

$z-index: 10;

.select-products__alert-icon {
  color: $color-primary-red;
}

.select-product__actions-container {
  position: sticky;
  top: $actions-top;
  @media (max-width: 768px) {
    top: $acttions-top-mobile;
    margin-bottom: 0;
  }

  width: 100%;

  background-color: white;
  z-index: $z-index;

  margin-bottom: 31px;
}

.select-product__actions {
  height: $actions-height;

  border-bottom: 1px solid $border-color;
  margin-bottom: 24px;

  div {
    flex: 1 1 0;
  }

  @media (max-width: 768px) {
    height: $actions-height-mobile;
    flex-direction: column;
    align-items: center;

    .select-products__selected-msg {
      margin-top: 16px;
      margin-bottom: 16px;
    }
  }
}

.status-checkbox {
  @include font-size(14px, 20px);
}

.filters-dropdown {
  justify-content: space-between;
  flex: 1 1 0;
  :deep() .dropdown-filters {
    .input-group {
      margin: 0;
    }

    .filters-area__row:not(:first-child) {
      justify-content: start;
    }
  }

  .tags-filter {
    :deep() .tags-wrapper {
      margin-bottom: 0;
      padding-bottom: 0;
      border-bottom: none;
    }

    :deep() .tags-wrapper__selected {
      padding-bottom: 22px;
    }

    :deep() .input-group.search {
      display: flex;
      align-items: center;
      margin-bottom: 22px;
      font-weight: 600;
    }

    :deep() .input-group__label {
      margin-bottom: 0;
      margin-right: 16px;
    }
  }

  :deep() .ez-filter-list {
    &__items {
      margin: 0;
    }
  }
}

.select-products__selected-msg {
  @include font-size(14px, 22px);
  @extend %flex-center;
  justify-content: center;
  flex: 1 1 0;

  min-width: $action-min-width;
  color: $color-gray-6C;

  background-color: white;
}

.select-products__footer {
  position: fixed;
  bottom: 0;

  width: 1176px;
  height: 56px;

  background-color: white;
  border-top: 1px solid $border-color;

  @media (max-width: 768px) {
    width: 100%;
  }
}

.total {
  @extend %flex-center;
  justify-content: flex-end;
  @media (max-width: 768px) {
    justify-content: flex-start;
  }

  margin-top: 16px;

  &__label {
    margin-right: 4px;
    color: $color-gray-6C;
    font-size: 12px;
    font-weight: 600;
  }

  &__amount {
    @include font-size(18px);
    font-weight: bold;
    color: $color-gray-25;
  }
}

.wizard-products__table-actions-filters {
  flex: 1 1 0;
  :deep() .ez-filter-list__items {
    flex-grow: 0;
  }
}

:deep() .category-dropdown .ez-select__display-container {
  height: 2.25rem;
}
</style>
